import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle } from 'lucide-react';

const JobPaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(true);
  const [error, setError] = useState(null);
  
  const jobId = searchParams.get('jobId');
  const reference = searchParams.get('reference');
  const status = searchParams.get('status');

  useEffect(() => {
    const processPaymentSuccess = async () => {
      console.log('🔍 JobPaymentSuccess - URL params:', {
        jobId,
        reference,
        status,
        fullURL: window.location.href
      });

      if (!jobId) {
        setError('Missing job ID');
        setProcessing(false);
        return;
      }

      try {
        const token = localStorage.getItem('token');
        console.log('🔑 Token exists:', !!token);
        
        const requestBody = {
          jobId,
          reference,
          status,
          paymentStatus: status === 'completed' ? 'PAID' : 'PENDING'
        };
        
        console.log('📤 Sending request to update payment status:', requestBody);

        // Call our backend to update the job payment status
        const response = await fetch('/api/jobs/update-payment-status', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(requestBody)
        });

        console.log('📥 Response status:', response.status);
        const responseData = await response.json();
        console.log('📥 Response data:', responseData);

        if (response.ok) {
          console.log('✅ Job payment status updated successfully');
          // Redirect to the job page after a short delay with a timestamp to force refresh
          setTimeout(() => {
            navigate(`/jobs/${jobId}?payment=success&status=${status}&t=${Date.now()}`);
          }, 2000);
        } else {
          console.error('❌ Failed to update job payment status:', responseData);
          // Still redirect to job page even if update fails
          setTimeout(() => {
            navigate(`/jobs/${jobId}?payment=success&status=${status}&t=${Date.now()}`);
          }, 2000);
        }
      } catch (error) {
        console.error('❌ Error processing payment success:', error);
        // Still redirect to job page even if there's an error
        setTimeout(() => {
          navigate(`/jobs/${jobId}?payment=success&status=${status}`);
        }, 2000);
      } finally {
        setProcessing(false);
      }
    };

    processPaymentSuccess();
  }, [jobId, reference, status, navigate]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6 text-center">
          <XCircle className="mx-auto h-12 w-12 text-red-500 mb-4" />
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Payment Error</h1>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6 text-center">
        <CheckCircle className="mx-auto h-12 w-12 text-green-500 mb-4" />
        <h1 className="text-xl font-semibold text-gray-900 mb-2">Payment Successful!</h1>
        
        {processing ? (
          <div className="mb-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
            <p className="text-gray-600">Processing your payment...</p>
          </div>
        ) : (
          <p className="text-gray-600 mb-4">Your payment has been processed successfully.</p>
        )}
        
        <div className="text-sm text-gray-500 mb-4">
          <p>Job ID: {jobId}</p>
          {reference && <p>Reference: {reference}</p>}
          <p>Status: {status}</p>
        </div>
        
        <p className="text-sm text-gray-600">
          Redirecting you to the job details page...
        </p>
      </div>
    </div>
  );
};

export default JobPaymentSuccess;